import { Dispatch } from 'react';
import { NavigateFunction } from 'react-router-dom';
import axios from 'axios';
import { attachTokenToHeaders } from '../auth/actions';
import { ItemsActionTypes, ItemsActions } from './types';
import { RootState } from 'store/reducers';
import { GlobalActions, GlobalActionTypes } from 'store/global/types';
import apiService from 'services/apiService';
import { IItem } from 'models/IItems';

type FormData = IItem & { token: string };
type ActionTypes = GlobalActions | ItemsActions;

export const uploadImages =
  (formData: any) => async (dispatch: Dispatch<ActionTypes>, getState: () => RootState) => {
    try {
      const options = attachTokenToHeaders(getState);
      const response = await apiService.post('/api/map-items/upload', formData, {
        ...options,
        headers: {
          ...options?.headers,
          'Content-Type': 'multipart/form-data',
        },
      });
      // Prefer server GPS; if null, keep any client-side GPS already in state (Android strips EXIF on upload)
      const existingGeoData = getState().mapItems.preImageGeoData;
      dispatch({
        type: ItemsActionTypes.GET_IMAGE_LOCATION_SUCCESS,
        payload: { preImageGeoData: response.data?.location ?? existingGeoData, uploaded: true },
      });
    } catch (err: any) {
      dispatch({
        type: GlobalActionTypes.MESSAGE_FAIL,
        payload: { error: err?.response?.data.message || err.message },
      });
    }
  };

export const getMapItem =
  (id: string) => async (dispatch: Dispatch<ActionTypes>, getState: () => RootState) => {
    dispatch({ type: GlobalActionTypes.LOADING });
    try {
      const options = attachTokenToHeaders(getState);
      const response = await apiService.get(`/api/map-items/${id}`, options);
      dispatch({
        type: ItemsActionTypes.GET_MAP_ITEM_SUCCESS,
        payload: { mapItem: response.data },
      });
      dispatch({
        type: GlobalActionTypes.SUCCESS_LOAD,
      });
    } catch (err: any) {
      dispatch({
        type: GlobalActionTypes.MESSAGE_FAIL,
        payload: { error: err?.response?.data.message || err.message },
      });
    }
  };

export const deleteImage = () => async (dispatch: Dispatch<ActionTypes>) => {
  dispatch({
    type: ItemsActionTypes.DELETE_IMAGE_SUCCESS,
    payload: { uploaded: false },
  });
};

export const getMapItems =
  (edit?: boolean) => async (dispatch: Dispatch<ActionTypes>, getState: () => RootState) => {
    dispatch({ type: GlobalActionTypes.LOADING });
    try {
      const options = attachTokenToHeaders(getState);
      const response = await apiService.get(`/api/map-items${edit ? `?edit=true` : ''}`, options);
      dispatch({
        type: ItemsActionTypes.GET_MAP_ITEMS_SUCCESS,
        payload: { mapItems: response.data },
      });
      dispatch({
        type: GlobalActionTypes.SUCCESS_LOAD,
      });
    } catch (err: any) {
      dispatch({
        type: GlobalActionTypes.MESSAGE_FAIL,
        payload: { error: err?.response?.data.message || err.message },
      });
    }
  };

export const getMapItemsPreview =
  () => async (dispatch: Dispatch<ActionTypes>, getState: () => RootState) => {
    dispatch({ type: GlobalActionTypes.LOADING });
    try {
      const options = attachTokenToHeaders(getState);
      const response = await apiService.get(`/api/map-items/preview`, options);
      dispatch({
        type: ItemsActionTypes.GET_MAP_ITEMS_SUCCESS,
        payload: { mapItems: response.data },
      });
      dispatch({
        type: GlobalActionTypes.SUCCESS_LOAD,
      });
    } catch (err: any) {
      dispatch({
        type: GlobalActionTypes.MESSAGE_FAIL,
        payload: { error: err?.response?.data.message || err.message },
      });
    }
  };

export const getMapItemDetails =
  (id: string) => async (dispatch: Dispatch<ActionTypes>, getState: () => RootState) => {
    try {
      const options = attachTokenToHeaders(getState);
      const response = await apiService.get(`/api/map-items/${id}`, options);
      
      // Images are already full URLs from Cloudinary
      const imageUrls = response.data.images?.map((img: any) => {
        if (typeof img === 'string') {
          return img; // Already a Cloudinary URL
        }
        return img.path; // img.path is the full Cloudinary URL
      }) || [];
      
      const mapItemDetails = {
        ...response.data,
        images: imageUrls,
      };
      
      dispatch({
        type: ItemsActionTypes.GET_MAP_ITEM_DETAILS_SUCCESS,
        payload: { mapItemDetails },
      });
    } catch (err: any) {
      dispatch({
        type: GlobalActionTypes.MESSAGE_FAIL,
        payload: { error: err?.response?.data.message || err.message },
      });
    }
  };

export const addMapItem =
  (formData: FormData, navigate: NavigateFunction) =>
  async (dispatch: Dispatch<ActionTypes>, getState: () => RootState) => {
    dispatch({ type: GlobalActionTypes.LOADING });
    try {
      const options = attachTokenToHeaders(getState);
      const response = await axios.post('/api/map-items', formData, options);

      dispatch({
        type: GlobalActionTypes.SUCCESS_LOAD,
      });

      navigate('/', { state: { message: response.data.message } });
    } catch (err: any) {
      dispatch({
        type: GlobalActionTypes.MESSAGE_FAIL,
        payload: { error: err?.response?.data.message || err?.response?.data || err.message },
      });
    }
  };

export const deleteMapItem =
  (id: number) => async (dispatch: Dispatch<ActionTypes>, getState: () => RootState) => {
    dispatch({ type: GlobalActionTypes.LOADING });
    try {
      const options = attachTokenToHeaders(getState);
      const response = await axios.delete(`/api/map-items/${id}`, options);

      dispatch({
        type: ItemsActionTypes.DELETE_MAP_ITEM_SUCCESS,
        payload: { message: response.data.message },
      });

      dispatch({
        type: GlobalActionTypes.SUCCESS_LOAD,
      });
    } catch (err: any) {
      dispatch({
        type: GlobalActionTypes.MESSAGE_FAIL,
        payload: { error: err?.response?.data.message || err.message },
      });
    }
  };

export const editMapItem =
  (id: number, formData: FormData) =>
  async (dispatch: Dispatch<ActionTypes>, getState: () => RootState) => {
    dispatch({ type: GlobalActionTypes.LOADING });
    try {
      const options = attachTokenToHeaders(getState);
      const response = await axios.put(`/api/map-items/${id}`, formData, options);

      dispatch({
        type: ItemsActionTypes.EDIT_MAP_ITEM_SUCCESS,
        payload: { mapItem: response.data.mapItem },
      });

      dispatch({
        type: GlobalActionTypes.SUCCESS_LOAD,
      });
    } catch (err: any) {
      dispatch({
        type: GlobalActionTypes.MESSAGE_FAIL,
        payload: { error: err?.response?.data.message || err.message },
      });
    }
  };

export const getComments =
  (mapItemId: string) => async (dispatch: Dispatch<ActionTypes>, getState: () => RootState) => {
    try {
      const options = attachTokenToHeaders(getState);
      const response = await axios.get(`/api/comments/${mapItemId}`, options);
      dispatch({
        type: ItemsActionTypes.GET_COMMENTS_SUCCESS,
        payload: {
          comments: response.data,
        },
      });

      dispatch({
        type: GlobalActionTypes.SUCCESS_LOAD,
      });
    } catch (err: any) {
      dispatch({
        type: GlobalActionTypes.MESSAGE_FAIL,
        payload: { error: err?.response?.data.message || err.message },
      });
    }
  };

export const addComment =
  (mapItemId: string, text: string, token: string, onSuccess: () => void) =>
  async (dispatch: Dispatch<ActionTypes>, getState: () => RootState) => {
    dispatch({ type: ItemsActionTypes.ADD_COMMENT_LOADING });

    try {
      const options = attachTokenToHeaders(getState);
      const response = await apiService.post(
        `/api/comments/${mapItemId}`,
        { text, mapItemId, token },
        options,
      );
      dispatch({
        type: ItemsActionTypes.ADD_COMMENT_SUCCESS,
        payload: {
          comment: response.data,
        },
      });
      dispatch({
        type: GlobalActionTypes.SUCCESS_LOAD,
      });

      onSuccess();
    } catch (err: any) {
      dispatch({
        type: ItemsActionTypes.ADD_COMMENT_FAIL,
      });
      dispatch({
        type: GlobalActionTypes.MESSAGE_FAIL,
        payload: { error: err?.response?.data.message || err.message },
      });
    }
  };

export const getLikes =
  (commentId: string, userId: number | undefined) =>
  async (dispatch: Dispatch<ActionTypes>, getState: () => RootState) => {
    try {
      const options = attachTokenToHeaders(getState);
      const response = await apiService.get(`/api/comments/${commentId}/likes`, {
        params: { userId },
        ...options,
      });

      dispatch({
        type: ItemsActionTypes.GET_LIKES_SUCCESS,
        payload: {
          likesCount: response.data.likesCount,
          userLiked: response.data.userLiked,
          commentId,
        },
      });

      dispatch({
        type: GlobalActionTypes.SUCCESS_LOAD,
      });
    } catch (err: any) {
      dispatch({
        type: GlobalActionTypes.MESSAGE_FAIL,
        payload: { error: err?.response?.data.message || err.message },
      });
    }
  };

export const toggleLike =
  (commentId: string) => async (dispatch: Dispatch<ActionTypes>, getState: () => RootState) => {
    try {
      const options = attachTokenToHeaders(getState);
      const response = await apiService.post(`/api/comments/${commentId}/like`, {}, options);

      dispatch({
        type: ItemsActionTypes.TOGGLE_LIKE_SUCCESS,
        payload: {
          liked: response.data.liked,
          likesCount: response.data.likesCount,
          commentId,
        },
      });

      dispatch({
        type: GlobalActionTypes.SUCCESS_LOAD,
      });
    } catch (err: any) {
      dispatch({
        type: GlobalActionTypes.MESSAGE_FAIL,
        payload: { error: err?.response?.data.message || err.message },
      });
    }
  };

export const clearMapItemError = (id: number) => ({
  type: ItemsActionTypes.CLEAR_MAP_ITEM_ERROR,
  payload: { id },
});

export const clearMapItemDetails = () => ({
  type: ItemsActionTypes.CLEAR_MAP_ITEM_DETAILS,
});
